// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useBackendStatus } from "@/hooks/useBackendStatus"
import { maintenanceStore } from "@/stores/maintenance"

// M7 MaintenanceView — full-screen overlay shown when the Backend is
// detected as down (M6 MaintenanceDetector sets the flag).
// Mounting multiple instances is safe: each reads the same store, all render consistently.
// Time zone is hard-coded to Europe/Paris (matches the ECS auto-stop schedule).
// Between 19:00 and 07:00 Paris time the component shows "Back at 07:00 (Paris)";
// otherwise "Backend temporarily unavailable — retrying".
export default function MaintenanceView() {
  const { isBackendDown } = useBackendStatus()

  if (!isBackendDown) {
    return null
  }

  const parisHour = getParisHour()
  const message =
    parisHour >= 19 || parisHour < 7
      ? "Back at 07:00 (Paris)"
      : "Backend temporarily unavailable — retrying"

  function handleTryAgain() {
    // Probe the Backend (not the frontend origin) — in production the two
    // live on different hosts (Cloudflare Pages vs AWS ALB).
    const backendUrl = import.meta.env.VITE_BACKEND_URL ?? ""
    fetch(`${backendUrl}/healthz`)
      .then((res) => {
        if (res.ok) {
          maintenanceStore.setBackendDown(false)
          maintenanceStore.resetFailures()
        }
      })
      .catch(() => {
        // Network down — keep overlay; nothing to do, no console noise.
      })
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background text-foreground">
      <h1 className="text-3xl font-bold mb-4">Ebiose is sleeping</h1>
      <p className="text-lg mb-8">{message}</p>
      <button
        onClick={handleTryAgain}
        className="px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        Try again
      </button>
    </div>
  )
}

function getParisHour(): number {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris",
    hour: "numeric",
    hour12: false,
  })
  const now = new Date()
  return parseInt(formatter.format(now), 10)
}
